import React from 'react';
import { Rotate, Star, AlertTriangle } from 'tabler-icons-react';
import { deltaPct } from '../utils/capacidades';

// Fila editable de capacidad, reutilizada por:
//  - la fila "Capacidad de línea" (fuente de verdad, esLinea=true)
//  - cada celda de la línea (esLinea=false), con modelo de herencia visible.
// Es presentacional: no hace fetch ni conoce el contexto; todo llega por props.
export default function CeldaCapRow({
    esLinea = false,
    esFuente = false,
    nombre,
    fields,
    valores,
    defaults,
    configurada = false,
    dirty = false,
    saving = false,
    canEdit = false,
    // En modo carga inicial (POST de golpe) las celdas se editan pero el guardado es
    // global a nivel de línea, así que se ocultan los botones guardar/reset por celda.
    sinAcciones = false,
    onChangeField,
    onGuardar,
    onReset,
}) {
    // Una celda hereda el default de la línea mientras no esté personalizada ni tocada.
    const heredado = !esLinea && !configurada && !dirty;

    return (
        <div
            className={`cap-celda-row${esLinea ? ' cap-celda-row--source' : ''}${saving ? ' cap-celda-row--saving' : ''}`}
        >
            <div className="cap-celda-name">
                {!esLinea && (esFuente ? (
                    <Star size={15} className="cap-celda-name-icon" />
                ) : (
                    !configurada && <AlertTriangle size={15} className="cap-celda-name-warn" />
                ))}
                <div className="cap-celda-name-text">
                    <span className="cap-celda-name-label">
                        {esLinea ? 'Capacidad de la línea' : nombre}
                    </span>
                    {esLinea ? (
                        !configurada && (
                            <span
                                className="cap-celda-badge cap-celda-badge--default"
                                title="Aún no existe una capacidad por default para esta línea"
                            >
                                Sin definir
                            </span>
                        )
                    ) : esFuente ? (
                        <span
                            className="cap-celda-badge cap-celda-badge--fuente"
                            title="El backend deriva la capacidad de la línea del conteo de esta celda"
                        >
                            Fuente de la línea
                        </span>
                    ) : (
                        <span
                            className={`cap-celda-badge cap-celda-badge--${configurada || dirty ? 'custom' : 'default'}`}
                        >
                            {configurada || dirty ? 'Personalizada' : 'Default'}
                        </span>
                    )}
                </div>
            </div>

            {fields.map((field) => {
                // Rollover solo aplica a la línea; en celdas se muestra un guion.
                if (field.lineOnly && !esLinea) {
                    return (
                        <div key={field.key} className="cap-celda-cell cap-celda-cell--na">—</div>
                    );
                }
                const value = valores[field.key];
                const delta = !esLinea ? deltaPct(value, defaults?.[field.key]) : null;
                return (
                    <div key={field.key} className="cap-celda-cell">
                        <input
                            type="number"
                            min={0}
                            step={field.float ? '0.01' : '1'}
                            title={field.hint}
                            className={`cap-celda-input${heredado ? ' cap-celda-input--inherited' : ''}${dirty ? ' cap-celda-input--dirty' : ''}`}
                            value={value ?? ''}
                            disabled={!canEdit}
                            onChange={(e) => {
                                const raw = e.target.value;
                                if (field.nullable && raw === '') {
                                    onChangeField(field.key, null);
                                    return;
                                }
                                const num = field.float ? parseFloat(raw) : parseInt(raw, 10);
                                onChangeField(field.key, Math.max(0, Number.isNaN(num) ? 0 : num));
                            }}
                        />
                        {delta != null && (
                            <span className={`cap-celda-delta cap-celda-delta--${delta >= 0 ? 'pos' : 'neg'}`}>
                                {delta >= 0 ? `+${delta}%` : `${delta}%`}
                            </span>
                        )}
                    </div>
                );
            })}

            <div className="cap-celda-actions">
                {!sinAcciones && canEdit && !esLinea && (configurada || dirty) && (
                    <button
                        type="button"
                        className="cap-celda-reset"
                        title="Restaurar al default de línea"
                        onClick={onReset}
                        disabled={saving}
                    >
                        <Rotate size={16} />
                    </button>
                )}
                {!sinAcciones && canEdit && dirty && (
                    <button
                        type="button"
                        className="cap-celda-save"
                        onClick={onGuardar}
                        disabled={saving}
                    >
                        {saving ? 'Guardando…' : 'Guardar'}
                    </button>
                )}
            </div>
        </div>
    );
}
